import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Tooltip, Chip, useMediaQuery, useTheme, IconButton, Collapse } from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Schedule as ScheduleIcon,
  WeekendOutlined as WeekendIcon,
  GridView as GridViewIcon,
  Bolt as BoltIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

// Market session definitions with UTC times (Forex/Crypto hours)
export const MARKET_SESSIONS = [
  {
    id: 'sydney',
    name: 'Sydney',
    shortName: 'SYD',
    flag: '\u{1F1E6}\u{1F1FA}',
    timezone: 'Australia/Sydney',
    openHourUTC: 22,
    closeHourUTC: 7,
    color: '#475569',
    goldenHours: { start: 22, end: 1 },
    preMarketMinutes: 30,
  },
  {
    id: 'tokyo',
    name: 'Tokyo',
    shortName: 'TYO',
    flag: '\u{1F1EF}\u{1F1F5}',
    timezone: 'Asia/Tokyo',
    openHourUTC: 0,
    closeHourUTC: 9,
    color: '#8B5CF6',
    goldenHours: { start: 0, end: 3 },
    preMarketMinutes: 30,
  },
  {
    id: 'frankfurt',
    name: 'Frankfurt',
    shortName: 'FRA',
    flag: '\u{1F1E9}\u{1F1EA}',
    timezone: 'Europe/Berlin',
    openHourUTC: 7,
    closeHourUTC: 16,
    color: '#F59E0B',
    goldenHours: { start: 7, end: 10 },
    preMarketMinutes: 30,
  },
  {
    id: 'london',
    name: 'London',
    shortName: 'LON',
    flag: '\u{1F1EC}\u{1F1E7}',
    timezone: 'Europe/London',
    openHourUTC: 8,
    closeHourUTC: 17,
    color: '#06B6D4',
    goldenHours: { start: 8, end: 11 },
    preMarketMinutes: 30,
  },
  {
    id: 'newyork',
    name: 'New York',
    shortName: 'NYC',
    flag: '\u{1F1FA}\u{1F1F8}',
    timezone: 'America/New_York',
    openHourUTC: 13,
    closeHourUTC: 22,
    color: '#10B981',
    goldenHours: { start: 13, end: 16 },
    preMarketMinutes: 60,
  }
];

// Check if hour is within range (handles overnight)
const isHourInRange = (hour, start, end) => {
  if (start < end) return hour >= start && hour < end;
  return hour >= start || hour < end;
};

// Get session status
export const getSessionStatus = (session, hourUTC, isWeekendDay) => {
  if (isWeekendDay) return { isOpen: false, isGolden: false, status: 'weekend' };
  const isOpen = isHourInRange(hourUTC, session.openHourUTC, session.closeHourUTC);
  const isGolden = isOpen && isHourInRange(hourUTC, session.goldenHours.start, session.goldenHours.end);
  return { isOpen, isGolden, status: isGolden ? 'golden' : isOpen ? 'open' : 'closed' };
};

// Check if any market is open
export const isAnyMarketOpen = (hourUTC, isWeekendDay) => {
  if (isWeekendDay) return false;
  return MARKET_SESSIONS.some(s => isHourInRange(hourUTC, s.openHourUTC, s.closeHourUTC));
};

// Get golden hour sessions
export const getGoldenHourSessions = (hourUTC, isWeekendDay) => {
  if (isWeekendDay) return [];
  return MARKET_SESSIONS.filter(s => {
    const { isGolden } = getSessionStatus(s, hourUTC, isWeekendDay);
    return isGolden;
  });
};

// Format local time
const getLocalTime = (timezone) => {
  try {
    return new Date().toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch { return '--:--'; }
};

// Calculate remaining time (hours, minutes) until open or close
const getTimeRemaining = (session, nowUTC) => {
  const hourUTC = nowUTC.getUTCHours();
  const minUTC = nowUTC.getUTCMinutes();
  const currentMinutes = hourUTC * 60 + minUTC;
  const isOpen = isHourInRange(hourUTC, session.openHourUTC, session.closeHourUTC);

  let targetMinutes;
  if (isOpen) {
    targetMinutes = session.closeHourUTC * 60;
    if (session.closeHourUTC <= session.openHourUTC) targetMinutes += 24 * 60;
    if (currentMinutes > targetMinutes) targetMinutes += 24 * 60;
  } else {
    targetMinutes = session.openHourUTC * 60;
    if (targetMinutes <= currentMinutes) targetMinutes += 24 * 60;
  }

  let diff = targetMinutes - currentMinutes;
  if (diff < 0) diff += 24 * 60;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return { hours: h, minutes: m, totalMinutes: diff, isOpen };
};

// Check if session is in pre-market window
const isPreMarket = (session, nowUTC) => {
  const { totalMinutes, isOpen } = getTimeRemaining(session, nowUTC);
  if (isOpen) return false;
  return totalMinutes <= (session.preMarketMinutes || 30);
};

// Deterministic pseudo-random based on seed (avoids flicker on re-render)
const seededRandom = (seed) => {
  let x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
};

// Keyframe styles injected once
const keyframeStyles = `
@keyframes mhm-pulse { 0%,100%{opacity:1} 50%{opacity:.6} }
@keyframes mhm-shimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(100%)} }
@keyframes mhm-glow-pulse { 0%,100%{box-shadow:0 0 8px rgba(217,70,239,.4)} 50%{box-shadow:0 0 18px rgba(217,70,239,.7)} }
@keyframes mhm-spin-slow { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
@keyframes mhm-ping { 75%,100%{transform:scale(2);opacity:0} }
@keyframes mhm-charging { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
`;

const MarketSessionsMonitor = ({ compact = false, onSessionChange = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('mhm-collapsed') === 'true');
  const toggleCollapsed = () => setCollapsed(prev => { const next = !prev; localStorage.setItem('mhm-collapsed', String(next)); return next; });
  const [useLocalTime, setUseLocalTime] = useState(() => localStorage.getItem('mhm-clock-mode') === 'local');
  const toggleClockMode = (e) => { e.stopPropagation(); setUseLocalTime(prev => { const next = !prev; localStorage.setItem('mhm-clock-mode', next ? 'local' : 'utc'); return next; }); };

  const updateSessions = useCallback(() => {
    const now = new Date();
    setCurrentTime(now);
    const hourUTC = now.getUTCHours();
    const dayUTC = now.getUTCDay();
    const isWeekendDay = dayUTC === 0 || dayUTC === 6;

    const updated = MARKET_SESSIONS.map(session => {
      const { isOpen, isGolden, status } = getSessionStatus(session, hourUTC, isWeekendDay);
      const remaining = getTimeRemaining(session, now);
      const preMkt = isPreMarket(session, now);
      return {
        ...session,
        isOpen,
        isGolden,
        status,
        localTime: getLocalTime(session.timezone),
        remaining,
        isPreMarket: preMkt,
      };
    });

    setSessions(updated);
    if (onSessionChange) {
      onSessionChange({
        sessions: updated,
        isWeekend: isWeekendDay,
        anyOpen: updated.some(s => s.isOpen),
        goldenSessions: updated.filter(s => s.isGolden)
      });
    }
  }, [onSessionChange]);

  useEffect(() => {
    updateSessions();
    const interval = setInterval(updateSessions, 10000);
    return () => clearInterval(interval);
  }, [updateSessions]);

  const hourUTC = currentTime.getUTCHours();
  const minuteUTC = currentTime.getUTCMinutes();
  const secondUTC = currentTime.getUTCSeconds();
  const currentPos = ((hourUTC + minuteUTC / 60 + secondUTC / 3600) / 24) * 100;
  const isWeekendDay = currentTime.getUTCDay() === 0 || currentTime.getUTCDay() === 6;
  const openSessions = sessions.filter(s => s.isOpen);
  const goldenSessions = sessions.filter(s => s.isGolden);

  // Find next session to open (for header countdown)
  const nextOpening = useMemo(() => {
    const closed = sessions.filter(s => !s.isOpen && !isWeekendDay);
    if (closed.length === 0) return null;
    closed.sort((a, b) => a.remaining.totalMinutes - b.remaining.totalMinutes);
    return closed[0];
  }, [sessions, isWeekendDay]);

  const utcTimeStr = `${String(hourUTC).padStart(2, '0')}:${String(minuteUTC).padStart(2, '0')}:${String(secondUTC).padStart(2, '0')}`;
  const localHour = currentTime.getHours();
  const localMinute = currentTime.getMinutes();
  const localSecond = currentTime.getSeconds();
  const localTimeStr = `${String(localHour).padStart(2, '0')}:${String(localMinute).padStart(2, '0')}:${String(localSecond).padStart(2, '0')}`;
  const localTzAbbr = Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(currentTime).find(p => p.type === 'timeZoneName')?.value || 'Local';
  const displayTimeStr = useLocalTime ? localTimeStr : utcTimeStr;
  const displayTzLabel = useLocalTime ? localTzAbbr : 'UTC';

  // Compact view
  if (compact) {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        {isWeekendDay ? (
          <Chip size="small" icon={<WeekendIcon sx={{ fontSize: 12 }} />} label="Weekend" 
            sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'grey.700' }} />
        ) : openSessions.length === 0 ? (
          <Typography variant="caption" color="text.secondary">Markets Closed</Typography>
        ) : (
          openSessions.map(s => (
            <Chip key={s.id} size="small" label={`${s.flag} ${s.name}`}
              sx={{
                height: 20, fontSize: '0.65rem', bgcolor: s.isGolden ? '#F59E0B' : s.color, color: 'white',
                animation: s.isGolden ? 'mhm-pulse 2s infinite' : 'none',
              }} />
          ))
        )}
      </Box>
    );
  }

  // Session bar height
  const barH = isMobile ? 32 : 38;
  const barGap = isMobile ? 6 : 8;
  const timelineHeight = sessions.length * (barH + barGap) + 40;

  // Full redesigned view
  return (
    <Box sx={{
      background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(30,27,75,0.6) 100%)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 3,
      overflow: 'hidden',
      position: 'relative',
      animation: 'fadeIn 0.4s ease-out',
      '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
    }}>
      <style>{keyframeStyles}</style>

      {/* ═══ HEADER ═══ */}
      <Box
        onClick={toggleCollapsed}
        sx={{
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1, sm: collapsed ? 1.2 : 2 },
          borderBottom: collapsed ? 'none' : '1px solid rgba(255,255,255,0.05)',
          background: 'rgba(0,0,0,0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'padding 0.3s ease',
          '&:hover': { background: 'rgba(255,255,255,0.02)' },
        }}
      >
        {/* Left: icon + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flexShrink: 0 }}>
          <Box sx={{
            p: 0.6, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1.5,
            border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <GridViewIcon sx={{ fontSize: 16, color: '#60A5FA' }} />
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography sx={{ fontSize: collapsed ? '0.85rem' : '1.1rem', fontWeight: 700, color: 'white', lineHeight: 1.2, transition: 'font-size 0.3s' }}>
              Market Heatmap
            </Typography>
            {!collapsed && (
              <Typography sx={{ fontSize: '0.6rem', color: 'rgba(156,163,175,1)', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                Volatility Intensity Index
              </Typography>
            )}
          </Box>
          {isWeekendDay && (
            <Chip size="small" icon={<WeekendIcon sx={{ fontSize: 11, color: 'white' }} />} label="Weekend"
              sx={{ height: 20, fontSize: '0.55rem', bgcolor: 'rgba(100,116,139,0.4)', color: 'white', border: '1px solid rgba(100,116,139,0.3)' }} />
          )}
        </Box>

        {/* Center: mini timeline (visible when collapsed) */}
        {collapsed && !isWeekendDay && (
          <Box sx={{ flex: 1, mx: { xs: 1, sm: 3 }, position: 'relative', height: 28, minWidth: 120 }}>
            {/* Mini 24h bar background */}
            <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 4, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, transform: 'translateY(-50%)' }} />
            {/* Session segments */}
            {sessions.map((s) => {
              const startPct = (s.openHourUTC / 24) * 100;
              const needsWrap = s.openHourUTC > s.closeHourUTC;
              const widthPct = needsWrap
                ? ((24 - s.openHourUTC) / 24) * 100
                : ((s.closeHourUTC - s.openHourUTC) / 24) * 100;
              const opacity = s.isOpen ? 0.9 : 0.25;
              return (
                <React.Fragment key={s.id}>
                  <Tooltip arrow title={`${s.flag} ${s.name} — ${s.isGolden ? 'Golden Hour' : s.isOpen ? 'Open' : s.isPreMarket ? 'Pre-Market' : 'Closed'} (${s.localTime})`}>
                    <Box sx={{
                      position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                      left: `${startPct}%`, width: `${widthPct}%`, height: 4, borderRadius: 2,
                      bgcolor: s.color, opacity, transition: 'opacity 0.3s',
                      boxShadow: s.isOpen ? `0 0 6px ${s.color}60` : 'none',
                    }} />
                  </Tooltip>
                  {/* Wrap-around for overnight sessions */}
                  {needsWrap && (
                    <Box sx={{
                      position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                      left: 0, width: `${(s.closeHourUTC / 24) * 100}%`, height: 4, borderRadius: 2,
                      bgcolor: s.color, opacity, transition: 'opacity 0.3s',
                    }} />
                  )}
                  {/* Flag circle */}
                  <Tooltip arrow title={`${s.flag} ${s.name} ${s.isOpen ? '● OPEN' : '○ Closed'}`}>
                    <Box sx={{
                      position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)',
                      left: `${startPct}%`,
                      width: s.isOpen ? 18 : 14, height: s.isOpen ? 18 : 14,
                      borderRadius: '50%',
                      bgcolor: s.isOpen ? s.color : 'rgba(30,30,40,0.9)',
                      border: `2px solid ${s.isOpen ? s.color : s.color + '60'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: s.isOpen ? '0.55rem' : '0.45rem',
                      boxShadow: s.isOpen ? `0 0 8px ${s.color}80` : 'none',
                      zIndex: 5, transition: 'all 0.3s',
                      animation: s.isGolden ? 'mhm-pulse 2s infinite' : 'none',
                    }}>
                      {s.flag}
                    </Box>
                  </Tooltip>
                </React.Fragment>
              );
            })}
            {/* Now needle */}
            <Box sx={{
              position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)',
              left: `${currentPos}%`, width: 2, height: 20,
              bgcolor: '#D946EF', borderRadius: 1, zIndex: 10,
              boxShadow: '0 0 8px rgba(217,70,239,0.6)',
            }}>
              <Box sx={{ position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)', width: 6, height: 6, borderRadius: '50%', bgcolor: '#D946EF', boxShadow: '0 0 6px rgba(217,70,239,0.8)' }} />
            </Box>
          </Box>
        )}

        {/* Collapsed session status chips */}
        {collapsed && !isWeekendDay && (
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.8 }}>
            {sessions.map(s => (
              <Tooltip key={s.id} arrow title={`${s.flag} ${s.name} — ${s.isOpen ? `Open (${s.remaining.hours}h ${String(s.remaining.minutes).padStart(2,'0')}m left)` : `Closed (Opens in ${s.remaining.hours}h ${String(s.remaining.minutes).padStart(2,'0')}m)`}`}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4,
                  px: 0.8, py: 0.3, borderRadius: 1,
                  bgcolor: s.isOpen ? `${s.color}15` : 'transparent',
                  border: `1px solid ${s.isOpen ? s.color + '40' : 'rgba(255,255,255,0.06)'}`,
                }}>
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '50%',
                    bgcolor: s.isOpen ? s.color : '#6B7280',
                    boxShadow: s.isOpen ? `0 0 6px ${s.color}80` : 'none',
                    animation: s.isGolden ? 'mhm-pulse 1.5s infinite' : 'none',
                  }} />
                  <Typography sx={{ fontSize: '0.6rem', color: s.isOpen ? s.color : '#6B7280', fontFamily: '"JetBrains Mono", monospace', fontWeight: 600 }}>
                    {s.shortName}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexShrink: 0 }}>
          {/* Next opening countdown */}
          {nextOpening && !isWeekendDay && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: collapsed ? 1 : 1.5,
              bgcolor: `${nextOpening.color}15`,
              px: { xs: 1, sm: collapsed ? 1.5 : 2 }, py: collapsed ? 0.5 : 0.8,
              borderRadius: 2.5,
              border: `1px solid ${nextOpening.color}40`,
              boxShadow: `0 0 15px ${nextOpening.color}15`,
              position: 'relative', overflow: 'hidden',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, bgcolor: `${nextOpening.color}08`, animation: 'mhm-pulse 2s infinite' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', position: 'relative', zIndex: 1 }}>
                <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, color: `${nextOpening.color}CC`, textTransform: 'uppercase', letterSpacing: '0.12em', lineHeight: 1 }}>
                  {nextOpening.shortName} Opening
                </Typography>
                <Typography sx={{
                  color: nextOpening.color, fontFamily: '"JetBrains Mono", monospace',
                  fontSize: collapsed ? '0.85rem' : '1.1rem', fontWeight: 700, lineHeight: 1,
                  animation: 'mhm-pulse 2s infinite',
                }}>
                  {String(nextOpening.remaining.hours).padStart(2, '0')}h {String(nextOpening.remaining.minutes).padStart(2, '0')}m
                </Typography>
              </Box>
              {!collapsed && (
                <>
                  <Box sx={{ width: '1px', height: 28, bgcolor: `${nextOpening.color}30`, mx: 0.5, position: 'relative', zIndex: 1 }} />
                  <ScheduleIcon sx={{ fontSize: 20, color: nextOpening.color, position: 'relative', zIndex: 1, animation: 'mhm-spin-slow 10s linear infinite' }} />
                </>
              )}
            </Box>
          )}

          {/* UTC / Local Clock (clickable toggle) */}
          <Box
            onClick={toggleClockMode}
            sx={{
              display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-end',
              cursor: 'pointer', px: 1, py: 0.3, borderRadius: 1.5,
              border: '1px solid transparent',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' },
            }}
            title={`Click to switch to ${useLocalTime ? 'UTC' : 'local'} time`}
          >
            <Typography sx={{ fontSize: '0.5rem', color: useLocalTime ? '#60A5FA' : 'rgba(156,163,175,1)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.12em', transition: 'color 0.2s' }}>
              {displayTzLabel}
            </Typography>
            <Typography sx={{ color: 'white', fontFamily: '"JetBrains Mono", monospace', fontSize: collapsed ? '0.9rem' : '1.1rem', fontWeight: 600, lineHeight: 1 }}>
              {displayTimeStr}
            </Typography>
          </Box>

          {/* Collapse toggle */}
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
            sx={{
              color: '#9CA3AF', p: 0.5,
              bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: 'white' },
              transition: 'transform 0.3s',
              transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={!collapsed} timeout={400}>
      {/* ═══ TIMELINE BODY ═══ */}
      <Box sx={{
        px: { xs: 1.5, sm: 3 },
        py: { xs: 2, sm: 3 },
        position: 'relative',
        minHeight: { xs: timelineHeight + 20, sm: timelineHeight + 40 },
        background: 'linear-gradient(180deg, rgba(11,17,32,1) 0%, rgba(15,22,35,1) 100%)',
      }}>
        {/* Vertical grid lines */}
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'space-between', px: { xs: 2, sm: 5 }, pointerEvents: 'none', zIndex: 0 }}>
          {[0,1,2,3,4].map(i => (
            <Box key={i} sx={{ borderLeft: '1px dashed rgba(255,255,255,0.04)', height: '100%' }} />
          ))}
        </Box>

        {/* Hour labels top */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: { xs: 2, sm: 5 }, mb: 2 }}>
          {['00:00', '06:00', '12:00', '18:00', '24:00'].map(label => (
            <Typography key={label} sx={{ fontSize: '0.65rem', color: 'rgba(107,114,128,1)', fontFamily: '"JetBrains Mono", monospace', userSelect: 'none' }}>
              {label}
            </Typography>
          ))}
        </Box>

        {/* Session bars */}
        <Box sx={{ position: 'relative', zIndex: 10 }}>
          {sessions.map((session, idx) => {
            const startPos = (session.openHourUTC / 24) * 100;
            const needsWrap = session.openHourUTC > session.closeHourUTC;
            const width = needsWrap
              ? ((24 - session.openHourUTC + session.closeHourUTC) / 24) * 100
              : ((session.closeHourUTC - session.openHourUTC) / 24) * 100;
            const barLeft = needsWrap ? `${startPos}%` : `${startPos}%`;
            const barWidth = needsWrap ? `${((24 - session.openHourUTC) / 24) * 100}%` : `${width}%`;
            const sessionHours = needsWrap ? (24 - session.openHourUTC + session.closeHourUTC) : (session.closeHourUTC - session.openHourUTC);
            const isOpen = session.isOpen;
            const isClosed = !isOpen && !session.isPreMarket;
            const isPre = session.isPreMarket;
            const remaining = session.remaining;
            const remainStr = isOpen
              ? `${remaining.hours}h ${String(remaining.minutes).padStart(2, '0')}m left`
              : `Opens in ${remaining.hours}h ${String(remaining.minutes).padStart(2, '0')}m`;

            // Heatmap cells — simulate volatility intensity per hour block
            const heatCells = [];
            for (let h = 0; h < sessionHours; h++) {
              const hourInSession = (session.openHourUTC + h) % 24;
              const isGoldenHour = isHourInRange(hourInSession, session.goldenHours.start, session.goldenHours.end);
              const seed = idx * 100 + h;
              const intensity = isGoldenHour ? 0.5 + seededRandom(seed) * 0.3 : 0.1 + seededRandom(seed) * 0.2;
              heatCells.push(intensity);
            }

            return (
              <Box key={session.id} sx={{ position: 'relative', height: barH, mb: `${barGap}px`, opacity: isClosed ? 0.5 : 1, transition: 'opacity 0.3s' }}>
                {/* Main bar */}
                <Tooltip arrow placement="top" title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" fontWeight="bold">{session.flag} {session.name}</Typography>
                    <Typography variant="caption" display="block">Local: {session.localTime}</Typography>
                    <Typography variant="caption" display="block">
                      Status: {session.isGolden ? 'Golden Hour' : session.isOpen ? 'Open' : session.isPreMarket ? 'Pre-Market' : 'Closed'}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                      Hours: {session.openHourUTC}:00 - {session.closeHourUTC}:00 UTC
                    </Typography>
                    <Typography variant="caption" display="block">{remainStr}</Typography>
                  </Box>
                }>
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: barLeft,
                    width: barWidth,
                    height: '100%',
                    borderRadius: 2,
                    border: isOpen
                      ? `1px solid ${session.color}50`
                      : `1px solid ${session.color}25`,
                    borderLeft: isOpen ? `3px solid ${session.color}` : `1px solid ${session.color}25`,
                    background: 'rgba(15,23,42,0.3)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: { xs: 1, sm: 1.5 },
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': { borderColor: `${session.color}80`, transform: 'scaleY(1.05)' },
                  }}>
                    {/* Heatmap grid inside bar */}
                    <Box sx={{
                      position: 'absolute', inset: 0, display: 'grid',
                      gridTemplateColumns: `repeat(${heatCells.length}, 1fr)`,
                      zIndex: 1, opacity: isOpen ? 0.8 : 0.3,
                    }}>
                      {heatCells.map((intensity, i) => (
                        <Box key={i} sx={{
                          bgcolor: `${session.color}`,
                          opacity: intensity,
                          borderRight: '1px solid rgba(0,0,0,0.1)',
                          transition: 'opacity 0.5s',
                        }} />
                      ))}
                    </Box>

                    {/* Shimmer for pre-market */}
                    {isPre && (
                      <Box sx={{
                        position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
                        overflow: 'hidden',
                        '&::after': {
                          content: '""',
                          position: 'absolute', inset: 0,
                          background: 'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)',
                          animation: 'mhm-shimmer 2s infinite linear',
                        }
                      }} />
                    )}

                    {/* Label */}
                    <Typography sx={{
                      fontSize: { xs: '0.65rem', sm: '0.72rem' },
                      color: isOpen ? 'white' : 'rgba(209,213,219,1)',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                      zIndex: 10,
                      whiteSpace: 'nowrap',
                      textShadow: isOpen ? `0 0 8px ${session.color}80` : 'none',
                    }}>
                      <span style={{ fontSize: isMobile ? '0.85rem' : '1rem' }}>{session.flag}</span>
                      {session.shortName}
                    </Typography>

                    {/* Status badge */}
                    <Box sx={{ zIndex: 10, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isPre && (
                        <>
                          {/* Charging ring for pre-market */}
                          <Box sx={{ position: 'relative', width: 20, height: 20, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{
                              position: 'absolute', inset: 0, borderRadius: '50%',
                              background: `conic-gradient(from 0deg, transparent 0%, ${session.color}CC 70%, transparent 100%)`,
                              animation: 'mhm-charging 2s linear infinite',
                            }} />
                            <Box sx={{ position: 'absolute', inset: '2px', bgcolor: 'rgba(11,17,32,1)', borderRadius: '50%', zIndex: 1 }} />
                            <BoltIcon sx={{ fontSize: 11, color: session.color, position: 'relative', zIndex: 2, animation: 'mhm-pulse 1.5s infinite' }} />
                          </Box>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            bgcolor: 'rgba(0,0,0,0.4)', px: 1, py: 0.3, borderRadius: 1,
                            backdropFilter: 'blur(8px)', border: `1px solid ${session.color}30`,
                          }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#FBBF24', animation: 'mhm-pulse 1s infinite' }} />
                            <Typography sx={{ fontSize: '0.55rem', color: '#FBBF24', fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, letterSpacing: '0.02em' }}>
                              PRE-MKT &bull; {remaining.minutes}m
                            </Typography>
                          </Box>
                        </>
                      )}
                      {isOpen && (
                        <Box sx={{
                          display: 'flex', alignItems: 'center', gap: 0.5,
                          bgcolor: 'rgba(0,0,0,0.4)', px: 1, py: 0.3, borderRadius: 1,
                          backdropFilter: 'blur(8px)', border: `1px solid ${session.color}30`,
                        }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: session.color, animation: 'mhm-pulse 1.5s infinite' }} />
                          <Typography sx={{ fontSize: '0.55rem', color: session.color, fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, letterSpacing: '0.02em' }}>
                            OPEN &bull; {remaining.hours}h {String(remaining.minutes).padStart(2, '0')}m left
                          </Typography>
                        </Box>
                      )}
                      {isClosed && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1, zIndex: 10 }}>
                          <Typography sx={{ fontSize: '0.55rem', color: 'rgba(156,163,175,0.7)', fontFamily: '"JetBrains Mono", monospace' }}>
                            CLOSED
                          </Typography>
                          <Typography sx={{ fontSize: '0.5rem', color: `${session.color}99`, fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.01em' }}>
                            Opens in {remaining.hours}h {String(remaining.minutes).padStart(2, '0')}m
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Tooltip>

                {/* Wrap-around second segment for overnight sessions */}
                {needsWrap && (
                  <Box sx={{
                    position: 'absolute', top: 0, left: 0,
                    width: `${(session.closeHourUTC / 24) * 100}%`,
                    height: '100%', borderRadius: 2,
                    border: isOpen ? `1px solid ${session.color}40` : `1px solid ${session.color}20`,
                    background: 'rgba(15,23,42,0.3)',
                    backdropFilter: 'blur(4px)',
                    overflow: 'hidden',
                  }}>
                    <Box sx={{
                      position: 'absolute', inset: 0, display: 'grid',
                      gridTemplateColumns: `repeat(${session.closeHourUTC}, 1fr)`,
                      opacity: isOpen ? 0.6 : 0.2,
                    }}>
                      {Array.from({ length: session.closeHourUTC }).map((_, i) => (
                        <Box key={i} sx={{
                          bgcolor: session.color,
                          opacity: 0.1 + seededRandom(idx * 100 + i + 50) * 0.15,
                          borderRight: '1px solid rgba(0,0,0,0.1)',
                        }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* ═══ NOW NEEDLE ═══ */}
        <Box sx={{
          position: 'absolute',
          top: 0, bottom: 0,
          left: `calc(${currentPos}% + ${isMobile ? 6 : 20}px)`,
          width: 2,
          background: 'linear-gradient(to bottom, transparent 0%, #D946EF 20%, #D946EF 80%, transparent 100%)',
          zIndex: 50,
          boxShadow: '0 0 10px rgba(217,70,239,0.5)',
          pointerEvents: 'none',
        }}>
          {/* Pin head */}
          <Box sx={{
            position: 'absolute',
            top: 6,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 64,
            bgcolor: 'rgba(26,11,46,0.92)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(217,70,239,0.5)',
            borderRadius: '8px 8px 4px 4px',
            p: '4px 8px 10px 8px',
            textAlign: 'center',
            boxShadow: '0 0 20px rgba(217,70,239,0.3)',
            animation: 'mhm-glow-pulse 3s infinite',
            clipPath: 'polygon(10% 0, 90% 0, 100% 55%, 50% 100%, 0% 55%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pb: 3,
            zIndex: 51,
          }}>
            <Typography sx={{ fontSize: '0.5rem', color: 'rgba(217,70,239,0.8)', textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700, lineHeight: 1 }}>
              Now
            </Typography>
            <Typography sx={{
              fontSize: '0.85rem', fontFamily: '"JetBrains Mono", monospace', fontWeight: 700,
              color: 'white', lineHeight: 1, mt: 0.2,
              textShadow: '0 0 6px rgba(255,255,255,0.5)',
            }}>
              {useLocalTime ? `${String(localHour).padStart(2, '0')}:${String(localMinute).padStart(2, '0')}` : `${String(hourUTC).padStart(2, '0')}:${String(minuteUTC).padStart(2, '0')}`}
            </Typography>
          </Box>

          {/* Particle dots on the line */}
          <Box sx={{ position: 'absolute', top: '65%', left: -2, width: 6, height: 6, borderRadius: '50%', bgcolor: 'white', animation: 'mhm-ping 1.5s infinite', opacity: 0.6 }} />
          <Box sx={{ position: 'absolute', top: '75%', left: -1, width: 4, height: 4, borderRadius: '50%', bgcolor: '#D946EF', animation: 'mhm-ping 2s infinite', opacity: 0.8 }} />
          <Box sx={{ position: 'absolute', top: '85%', left: -2, width: 5, height: 5, borderRadius: '50%', bgcolor: '#10B981', boxShadow: '0 0 10px rgba(16,185,129,0.8)', animation: 'mhm-ping 1.2s infinite' }} />
        </Box>

        {/* Hour numbers bottom */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between',
          px: { xs: 2, sm: 5 },
          mt: 2,
          borderTop: '1px solid rgba(255,255,255,0.05)',
          pt: 1.5,
        }}>
          {Array.from({ length: 24 }).map((_, i) => {
            const h = i + 1;
            const display = h <= 12 ? h : h - 12;
            const isCurrent = h === (hourUTC === 0 ? 24 : hourUTC) || h === hourUTC;
            return (
              <Typography key={i} sx={{
                flex: 1, textAlign: 'center',
                fontSize: { xs: '0.5rem', sm: '0.65rem' },
                fontWeight: isCurrent ? 700 : 500,
                color: isCurrent ? '#D946EF' : h > 12 ? 'rgba(156,163,175,0.5)' : 'rgba(107,114,128,0.6)',
                transform: isCurrent ? 'scale(1.15)' : 'none',
                textShadow: isCurrent ? '0 0 6px rgba(217,70,239,0.8)' : 'none',
                transition: 'all 0.3s',
                display: { xs: i % 2 === 0 ? 'block' : 'none', sm: 'block' },
              }}>
                {display}
              </Typography>
            );
          })}
        </Box>
      </Box>

      </Collapse>

      <Collapse in={!collapsed} timeout={400}>
      {/* ═══ FOOTER ═══ */}
      <Box sx={{
        px: { xs: 2, sm: 3 },
        py: 1.5,
        borderTop: '1px solid rgba(255,255,255,0.05)',
        bgcolor: 'rgba(11,17,32,1)',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 1.5,
      }}>
        {/* Open session chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {openSessions.map(s => (
            <Box key={s.id} sx={{
              bgcolor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              px: 1.5, py: 0.5, borderRadius: 5,
              display: 'flex', alignItems: 'center', gap: 0.8,
              backdropFilter: 'blur(4px)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              transition: 'background 0.2s',
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: s.color, boxShadow: `0 0 8px ${s.color}80` }} />
              <Typography sx={{ fontSize: '0.65rem', color: 'rgba(209,213,219,1)', fontWeight: 500 }}>
                {s.name} Market
              </Typography>
            </Box>
          ))}
          {openSessions.length === 0 && !isWeekendDay && (
            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(107,114,128,1)' }}>No markets currently open</Typography>
          )}
        </Box>

        {/* Volatility legend */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, color: 'rgba(107,114,128,1)' }}>
              Low Activity
            </Typography>
            <Typography sx={{ fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, color: 'rgba(107,114,128,1)' }}>
              Extreme Volatility
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            {[0.05, 0.1, 0.2, 0.35, 0.5, 0.65, 0.8, 1.0].map((opacity, i) => (
              <Box key={i} sx={{
                width: 14, height: 14, borderRadius: '2px',
                bgcolor: `rgba(217,70,239,${opacity})`,
                border: `1px solid rgba(217,70,239,${Math.min(opacity * 0.6, 0.5)})`,
                boxShadow: opacity > 0.6 ? `0 0 ${opacity * 12}px rgba(217,70,239,${opacity * 0.5})` : 'none',
              }} />
            ))}
          </Box>
        </Box>

        {/* Info badges */}
        <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 2, pl: 2, borderLeft: '1px solid rgba(255,255,255,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimelineIcon sx={{ fontSize: 13, color: '#D946EF' }} />
            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(156,163,175,1)', fontWeight: 500 }}>Real-time Analysis</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GridViewIcon sx={{ fontSize: 13, color: '#D946EF' }} />
            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(107,114,128,1)' }}>Heatmap Active</Typography>
          </Box>
        </Box>
      </Box>
      </Collapse>
    </Box>
  );
};

export default MarketSessionsMonitor;
